import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Tab, Product } from '@/types';
import { productInventoryService } from '@/services/products';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useContext } from 'react';
import { toast as sonnerToast } from 'sonner';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { queryKeys } from '@/lib/queryKeys';
import { FEATURE_FLAGS } from '@/types/featureFlags';

import { calculateWeightLinePrice, getPricePerKg } from '@/utils/weightPricing';

export const useUpdateTabItem = (currentOrganization: any) => {
  const queryClient = useQueryClient();
  const orgId = currentOrganization?.id ?? '';
  const tabsKey = queryKeys.tabs(orgId);
  const productsKey = queryKeys.products(orgId);
  const ingredientsKey = queryKeys.ingredients(orgId);
  const externalOrdersKey = queryKeys.externalOrders(orgId);
  const { translator } = useContext(LanguageProviderContext);
  const { isEnabled } = useFeatureFlag();
  const createKitchenTickets = isEnabled(FEATURE_FLAGS.KITCHEN_VIEW);

  return useMutation({
    mutationFn: async ({ 
      itemId, 
      quantity, 
      discount,
      notes,
      weightKg,
    }: { 
      itemId: string; 
      quantity?: number; 
      discount?: number;
      notes?: string | null;
      weightKg?: number;
    }) => {
      if (weightKg !== undefined) {
        return productInventoryService.updateTabItemWeight(itemId, weightKg, createKitchenTickets);
      }
      if (quantity !== undefined) {
        return productInventoryService.updateTabItemQuantity(itemId, quantity, createKitchenTickets);
      } else if (discount !== undefined) {
        return productInventoryService.updateTabItemDiscount(itemId, discount);
      } else if (notes !== undefined) {
        return productInventoryService.updateTabItemNotes(itemId, notes);
      }
      return null;
    },
    onMutate: async ({ itemId, quantity, discount, notes, weightKg }) => {

      await queryClient.cancelQueries({ queryKey: tabsKey });
      await queryClient.cancelQueries({ queryKey: productsKey });
      await queryClient.cancelQueries({ queryKey: ingredientsKey });
      await queryClient.cancelQueries({ queryKey: externalOrdersKey });
      
      const previousTabs = queryClient.getQueryData(tabsKey);
      const previousProducts = queryClient.getQueryData(productsKey);
      const previousExternalOrders = queryClient.getQueryData(externalOrdersKey);

      let productId: string | null = null;
      let originalQuantity: number | null = null;
      let originalWeightKg: number | null = null;
      let tabId: string | null = null;

      const applyItemChange = (old: Tab[] = []) => {
        return old.map(tab => {
          const foundItem = tab.items.find(item => item.id === itemId);
          
          if (foundItem) {
            productId = foundItem.productId;
            originalQuantity = foundItem.quantity;
            originalWeightKg =
              typeof foundItem.weightKg === 'number' ? foundItem.weightKg : null;
            tabId = tab.id;

            if (weightKg !== undefined) {
              if (weightKg <= 0) {
                return {
                  ...tab,
                  items: tab.items.filter(item => item.id !== itemId)
                };
              }
              const newUnitPrice = calculateWeightLinePrice(
                getPricePerKg({
                  unitPrice: foundItem.unitPrice,
                  weightKg: originalWeightKg ?? 0,
                  catalogPrice: foundItem.product.price,
                }),
                weightKg
              );
              return {
                ...tab,
                items: tab.items.map(item =>
                  item.id === itemId
                    ? { ...item, weightKg, unitPrice: newUnitPrice, quantity: 1 }
                    : item
                )
              };
            }
            
            if (quantity !== undefined) {
              if (quantity <= 0) {
                return {
                  ...tab,
                  items: tab.items.filter(item => item.id !== itemId)
                };
              } else {
                return {
                  ...tab,
                  items: tab.items.map(item => 
                    item.id === itemId 
                      ? { ...item, quantity } 
                      : item
                  )
                };
              }
            } else if (discount !== undefined) {
              return {
                ...tab,
                items: tab.items.map(item => 
                  item.id === itemId
                    ? { ...item, discount } 
                    : item
                )
              };
            } else if (notes !== undefined) {
              return {
                ...tab,
                items: tab.items.map(item => 
                  item.id === itemId
                    ? { ...item, notes: notes.trim() || undefined } 
                    : item
                )
              };
            }
          }
          
          return tab;
        });
      };

      queryClient.setQueryData(tabsKey, applyItemChange);
      queryClient.setQueryData(externalOrdersKey, (old: Tab[] | undefined) =>
        old ? applyItemChange(old) : old
      );

      if (weightKg !== undefined && productId && originalWeightKg != null) {
        const stockChange = originalWeightKg - Math.max(0, weightKg);
        queryClient.setQueryData(productsKey, (old: Product[] = []) => {
          return old.map(product => {
            if (product.id === productId) {
              return {
                ...product,
                stock: Math.max(0, product.stock + stockChange)
              };
            }
            return product;
          });
        });
      } else if (quantity !== undefined && productId && originalQuantity !== null) {
        const stockChange = originalQuantity - quantity;
        
        queryClient.setQueryData(productsKey, (old: Product[] = []) => {
          return old.map(product => {
            if (product.id === productId) {
              const newStock = Math.max(0, product.stock + stockChange);
              return {
                ...product,
                stock: newStock
              };
            }
            return product;
          });
        });
      }

      return { 
        previousTabs, 
        previousProducts,
        previousExternalOrders,
        context: { productId, originalQuantity, tabId, itemId }
      };
    },
    onSuccess: (result, product) => {
      if (product.weightKg !== undefined) {
        if (product.weightKg <= 0) {
          sonnerToast.success(translator('Tabs.productRemoved'), {
            description: translator('Tabs.productRemovedDesc'),
            duration: 3000,
          });
        } else {
          sonnerToast.success(translator('Tabs.weightUpdated'), {
            description: translator('Tabs.weightUpdatedDesc'),
            duration: 3000,
          });
        }
      } else if (product.quantity !== undefined) {
        if (product.quantity === 0) {
          sonnerToast.success(translator('Tabs.productRemoved'), {
            description: translator('Tabs.productRemovedDesc'),
            duration: 3000,
          });
        } else {
          sonnerToast.success(translator('Tabs.qtyUpdated'), {
            description: `${translator('Tabs.qtyUpdatedDesc')} ${product.quantity}`,
            duration: 3000,
          });
        }
      } else if (product.discount !== undefined) {
        sonnerToast.success(translator('Tabs.discApplied'), {
          description: `${translator('Tabs.discAppliedDesc1')} ${product.discount}${translator('Tabs.discAppliedDesc2')}`,
          duration: 3000,
        });
      }
    },
    onError: (err, variables, context) => {
      if (context?.previousTabs) {
        queryClient.setQueryData(tabsKey, context.previousTabs);
      }
      if (context?.previousProducts) {
        queryClient.setQueryData(productsKey, context.previousProducts);
      }
      if (context?.previousExternalOrders) {
        queryClient.setQueryData(externalOrdersKey, context.previousExternalOrders);
      }
      if (variables.weightKg !== undefined || variables.quantity !== undefined) {
        sonnerToast.error(translator('Tabs.errorUpdatingQty'), {
          description: translator('Tabs.errorUpdatingQtyDesc'),
          duration: 4000,
        });
      } else if (variables.discount !== undefined) {
        sonnerToast.error(translator('Tabs.errorUpdatingDisc'), {
          description: translator('Tabs.errorUpdatingDiscDesc'),
          duration: 4000,
        });
      } else if (variables.notes !== undefined) {
        sonnerToast.error(translator('Tabs.errorUpdatingNotes'), {
          description: translator('Tabs.errorUpdatingNotesDesc'),
          duration: 4000,
        });
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: tabsKey });
      queryClient.invalidateQueries({ queryKey: productsKey });
      queryClient.invalidateQueries({ queryKey: ingredientsKey });
      queryClient.invalidateQueries({ queryKey: queryKeys.externalOrders(orgId) });
      if (variables.quantity !== undefined || variables.weightKg !== undefined) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.tabItemDeletionHistoryAll(currentOrganization?.id ?? ''),
        });
      }

      if (variables.discount !== undefined) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.discountApplicationHistoryAll(currentOrganization?.id ?? ''),
        });
      }
     
    },
  });
};
